const {
  checkForRequirements,
  buildAnalyticsBaseContext,
  buildIndicatorQueryContext,
  getLocationTableGrouping,
  aggregateNationalLocationRows,
  calculateDeltaPct,
  roundTo2,
  roundOrNull
} = require('../utils')
const { getGrossProfitTotal, getGrossProfitTotalsByProduct, getGrossProfitTotalsByLocation } = require('./gross-profit')

const LOSSES_LOCATION_SUM_FIELDS = [
  'total_rows',
  'costed_rows',
  'missing_cost_rows',
  'other_losses_total',
  'cost_of_expiry_total',
  'losses_total',
  'current_month_losses_total',
  'previous_3_months_losses_total'
]

// A loss/expiry row without a resolved unit_cost yet is excluded from the totals below,
// the same way an uncosted sale is excluded from COGS on the gross profit side. The
// missing-costs banner is what flags the figures as incomplete.
const COSTED_ROW_CONDITION = 'unit_cost IS NOT NULL'

function appendLossesScopeAndProductFilters ({ params, whereClauses, locationScope, productSku, serviceIds }) {
  if (locationScope.scopeType !== 'national') {
    const exactIndex = params.length + 1
    const descendantIndex = params.length + 2
    whereClauses.push(`
        AND (
          dl.fsid = $${exactIndex}
          OR dl.fsid LIKE $${descendantIndex}
        )
    `)
    params.push(locationScope.location, `${locationScope.location}:%`)
  }

  if (productSku) {
    whereClauses.push(`AND li.product_sku_id = $${params.length + 1}`)
    params.push(productSku)
  }

  if (serviceIds && serviceIds.length > 0) {
    whereClauses.push(`AND li.service_id = ANY($${params.length + 1}::uuid[])`)
    params.push(serviceIds)
  }
}

async function getLossesTotals ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildIndicatorQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds,
    appendFilters: appendLossesScopeAndProductFilters
  })

  const query = `
      WITH filtered AS (
        SELECT
          li.reported_at,
          li.units_lost::numeric AS units_lost,
          li.units_expired::numeric AS units_expired,
          li.unit_cost
        FROM analytics.loss_indicators li
        INNER JOIN avocado.data_location dl
          ON dl.uuid = li.location_id
        WHERE li.organisation_id = $1
          AND li.reported_at >= $2::timestamptz
          AND li.reported_at < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        COUNT(*) FILTER (WHERE reported_at >= $6::timestamptz AND reported_at < $5::timestamptz)::int AS total_rows,
        COUNT(*) FILTER (WHERE ${COSTED_ROW_CONDITION} AND reported_at >= $6::timestamptz AND reported_at < $5::timestamptz)::int AS costed_rows,
        COUNT(*) FILTER (WHERE unit_cost IS NULL AND reported_at >= $6::timestamptz AND reported_at < $5::timestamptz)::int AS missing_cost_rows,
        COALESCE(
          SUM(units_lost * unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND reported_at >= $6::timestamptz
              AND reported_at < $5::timestamptz
          ),
          0
        )::numeric AS other_losses_total,
        COALESCE(
          SUM(units_expired * unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND reported_at >= $6::timestamptz
              AND reported_at < $5::timestamptz
          ),
          0
        )::numeric AS cost_of_expiry_total,
        COALESCE(
          SUM((COALESCE(units_lost, 0) + COALESCE(units_expired, 0)) * unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND reported_at >= $4::timestamptz
              AND reported_at < $5::timestamptz
          ),
          0
        )::numeric AS current_month_losses_total,
        COALESCE(
          SUM((COALESCE(units_lost, 0) + COALESCE(units_expired, 0)) * unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND reported_at >= $2::timestamptz
              AND reported_at < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_losses_total
      FROM filtered
    `

  const { rows } = await pgConnection.query(query, params)
  const row = rows[0] || {}
  const otherLossesTotal = Number(row.other_losses_total || 0)
  const costOfExpiryTotal = Number(row.cost_of_expiry_total || 0)
  const lossesTotal = roundTo2(otherLossesTotal + costOfExpiryTotal)
  const currentAverageDailyLosses = Number(row.current_month_losses_total || 0) / currentMonthDays
  const previous3MonthsAverageDailyLosses = Number(row.previous_3_months_losses_total || 0) / previous3MonthsPeriodDays
  const lossesDeltaPercent = calculateDeltaPct(currentAverageDailyLosses, previous3MonthsAverageDailyLosses)

  return {
    fromDate,
    toDate,
    organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    totalRows: Number(row.total_rows || 0),
    costedRows: Number(row.costed_rows || 0),
    missingCostRows: Number(row.missing_cost_rows || 0),
    otherLossesTotal: roundTo2(otherLossesTotal),
    costOfExpiryTotal: roundTo2(costOfExpiryTotal),
    lossesTotal,
    previous3MonthsAverageDailyLosses: roundTo2(previous3MonthsAverageDailyLosses),
    currentAverageDailyLosses: roundTo2(currentAverageDailyLosses),
    lossesDeltaPercent
  }
}

async function getLossesSummary ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  const [lossesTotals, grossProfitTotal] = await Promise.all([
    getLossesTotals({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }),
    getGrossProfitTotal({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds })
  ])

  const lossesPercentOfGrossProfit = grossProfitTotal === 0
    ? null
    : roundTo2((lossesTotals.lossesTotal / grossProfitTotal) * 100)
  const grossProfitAfterLosses = roundTo2(grossProfitTotal - lossesTotals.lossesTotal)

  return {
    ...lossesTotals,
    grossProfitTotal: roundTo2(grossProfitTotal),
    lossesPercentOfGrossProfit,
    grossProfitAfterLosses
  }
}

async function getLossesByProductTable ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildIndicatorQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds,
    appendFilters: appendLossesScopeAndProductFilters
  })

  const query = `
      WITH filtered AS (
        SELECT
          li.product_sku_id,
          li.reported_at,
          li.units_lost::numeric AS units_lost,
          li.units_expired::numeric AS units_expired,
          li.unit_cost
        FROM analytics.loss_indicators li
        INNER JOIN avocado.data_location dl
          ON dl.uuid = li.location_id
        WHERE li.organisation_id = $1
          AND li.reported_at >= $2::timestamptz
          AND li.reported_at < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        f.product_sku_id AS product_sku,
        COALESCE(ps.display_name, f.product_sku_id) AS product_name,
        COUNT(*) FILTER (WHERE f.reported_at >= $6::timestamptz AND f.reported_at < $5::timestamptz)::int AS total_rows,
        COUNT(*) FILTER (WHERE ${COSTED_ROW_CONDITION} AND f.reported_at >= $6::timestamptz AND f.reported_at < $5::timestamptz)::int AS costed_rows,
        COUNT(*) FILTER (WHERE f.unit_cost IS NULL AND f.reported_at >= $6::timestamptz AND f.reported_at < $5::timestamptz)::int AS missing_cost_rows,
        COALESCE(
          SUM(f.units_lost * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $6::timestamptz
              AND f.reported_at < $5::timestamptz
          ),
          0
        )::numeric AS other_losses_total,
        COALESCE(
          SUM(f.units_expired * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $6::timestamptz
              AND f.reported_at < $5::timestamptz
          ),
          0
        )::numeric AS cost_of_expiry_total,
        COALESCE(
          SUM((COALESCE(f.units_lost, 0) + COALESCE(f.units_expired, 0)) * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $4::timestamptz
              AND f.reported_at < $5::timestamptz
          ),
          0
        )::numeric AS current_month_losses_total,
        COALESCE(
          SUM((COALESCE(f.units_lost, 0) + COALESCE(f.units_expired, 0)) * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $2::timestamptz
              AND f.reported_at < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_losses_total
      FROM filtered f
      LEFT JOIN avocado.data_productsku ps
        ON ps.old_sku_id = f.product_sku_id
      WHERE f.product_sku_id IS NOT NULL
      GROUP BY f.product_sku_id, ps.display_name
      HAVING COUNT(*) FILTER (
        WHERE f.reported_at >= $6::timestamptz
          AND f.reported_at < $5::timestamptz
      ) > 0
      ORDER BY
        COALESCE(
          SUM(f.units_lost * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $6::timestamptz
              AND f.reported_at < $5::timestamptz
          ),
          0
        )
        +
        COALESCE(
          SUM(f.units_expired * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $6::timestamptz
              AND f.reported_at < $5::timestamptz
          ),
          0
        ) DESC NULLS LAST,
        product_sku ASC
    `

  const [{ rows }, grossProfitTotalsByProduct] = await Promise.all([
    pgConnection.query(query, params),
    getGrossProfitTotalsByProduct({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds })
  ])

  return {
    fromDate,
    toDate,
    organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    rows: rows.map((row) => {
      const otherLossesTotal = Number(row.other_losses_total || 0)
      const costOfExpiryTotal = Number(row.cost_of_expiry_total || 0)
      const lossesTotal = roundTo2(otherLossesTotal + costOfExpiryTotal)
      const currentAverageDailyLosses = Number(row.current_month_losses_total || 0) / currentMonthDays
      const previous3MonthsAverageDailyLosses = Number(row.previous_3_months_losses_total || 0) / previous3MonthsPeriodDays
      const grossProfitTotal = grossProfitTotalsByProduct.has(row.product_sku)
        ? grossProfitTotalsByProduct.get(row.product_sku)
        : null

      return {
        productSku: row.product_sku,
        productName: row.product_name,
        totalRows: Number(row.total_rows || 0),
        costedRows: Number(row.costed_rows || 0),
        missingCostRows: Number(row.missing_cost_rows || 0),
        otherLossesTotal: roundTo2(otherLossesTotal),
        costOfExpiryTotal: roundTo2(costOfExpiryTotal),
        lossesTotal,
        grossProfitTotal: roundOrNull(grossProfitTotal),
        lossesPercentOfGrossProfit: grossProfitTotal === null || grossProfitTotal <= 0
          ? null
          : roundTo2((lossesTotal / grossProfitTotal) * 100),
        previous3MonthsAverageDailyLosses: roundTo2(previous3MonthsAverageDailyLosses),
        currentAverageDailyLosses: roundTo2(currentAverageDailyLosses),
        lossesDeltaPercent: calculateDeltaPct(currentAverageDailyLosses, previous3MonthsAverageDailyLosses)
      }
    })
  }
}

async function getLossesByLocationTable ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds, aggregationLevel }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildIndicatorQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds,
    appendFilters: appendLossesScopeAndProductFilters
  })
  const grouping = getLocationTableGrouping(locationScope.location, aggregationLevel)

  const query = `
      WITH filtered AS (
        SELECT
          ${grouping.scopeExpression} AS location_fsid,
          li.reported_at,
          li.units_lost::numeric AS units_lost,
          li.units_expired::numeric AS units_expired,
          li.unit_cost
        FROM analytics.loss_indicators li
        INNER JOIN avocado.data_location dl
          ON dl.uuid = li.location_id
        WHERE li.organisation_id = $1
          AND li.reported_at >= $2::timestamptz
          AND li.reported_at < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        f.location_fsid AS location_fsid,
        COUNT(*) FILTER (WHERE f.reported_at >= $6::timestamptz AND f.reported_at < $5::timestamptz)::int AS total_rows,
        COUNT(*) FILTER (WHERE ${COSTED_ROW_CONDITION} AND f.reported_at >= $6::timestamptz AND f.reported_at < $5::timestamptz)::int AS costed_rows,
        COUNT(*) FILTER (WHERE f.unit_cost IS NULL AND f.reported_at >= $6::timestamptz AND f.reported_at < $5::timestamptz)::int AS missing_cost_rows,
        COALESCE(
          SUM(f.units_lost * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $6::timestamptz
              AND f.reported_at < $5::timestamptz
          ),
          0
        )::numeric AS other_losses_total,
        COALESCE(
          SUM(f.units_expired * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $6::timestamptz
              AND f.reported_at < $5::timestamptz
          ),
          0
        )::numeric AS cost_of_expiry_total,
        (
          COALESCE(
            SUM(f.units_lost * f.unit_cost) FILTER (
              WHERE ${COSTED_ROW_CONDITION}
                AND f.reported_at >= $6::timestamptz
                AND f.reported_at < $5::timestamptz
            ),
            0
          )
          +
          COALESCE(
            SUM(f.units_expired * f.unit_cost) FILTER (
              WHERE ${COSTED_ROW_CONDITION}
                AND f.reported_at >= $6::timestamptz
                AND f.reported_at < $5::timestamptz
            ),
            0
          )
        )::numeric AS losses_total,
        COALESCE(
          SUM((COALESCE(f.units_lost, 0) + COALESCE(f.units_expired, 0)) * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $4::timestamptz
              AND f.reported_at < $5::timestamptz
          ),
          0
        )::numeric AS current_month_losses_total,
        COALESCE(
          SUM((COALESCE(f.units_lost, 0) + COALESCE(f.units_expired, 0)) * f.unit_cost) FILTER (
            WHERE ${COSTED_ROW_CONDITION}
              AND f.reported_at >= $2::timestamptz
              AND f.reported_at < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_losses_total
      FROM filtered f
      WHERE f.location_fsid IS NOT NULL
      GROUP BY f.location_fsid
      ORDER BY
        (
          COALESCE(
            SUM(f.units_lost * f.unit_cost) FILTER (
              WHERE ${COSTED_ROW_CONDITION}
                AND f.reported_at >= $6::timestamptz
                AND f.reported_at < $5::timestamptz
            ),
            0
          )
          +
          COALESCE(
            SUM(f.units_expired * f.unit_cost) FILTER (
              WHERE ${COSTED_ROW_CONDITION}
                AND f.reported_at >= $6::timestamptz
                AND f.reported_at < $5::timestamptz
            ),
            0
          )
        ) DESC NULLS LAST,
        location_fsid ASC
    `

  const [{ rows }, grossProfitTotalsByLocation] = await Promise.all([
    pgConnection.query(query, params),
    getGrossProfitTotalsByLocation({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds, aggregationLevel })
  ])

  const groupedRows = locationScope.location === 'national'
    ? aggregateNationalLocationRows(rows, aggregationLevel, {
      sumFields: LOSSES_LOCATION_SUM_FIELDS,
      sortField: 'losses_total'
    })
    : rows

  return {
    fromDate,
    toDate,
    organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    rows: groupedRows.map((row) => {
      const otherLossesTotal = Number(row.other_losses_total || 0)
      const costOfExpiryTotal = Number(row.cost_of_expiry_total || 0)
      const lossesTotal = roundTo2(otherLossesTotal + costOfExpiryTotal)
      const currentAverageDailyLosses = Number(row.current_month_losses_total || 0) / currentMonthDays
      const previous3MonthsAverageDailyLosses = Number(row.previous_3_months_losses_total || 0) / previous3MonthsPeriodDays
      const grossProfitTotal = grossProfitTotalsByLocation.has(row.location_fsid)
        ? grossProfitTotalsByLocation.get(row.location_fsid)
        : null

      return {
        locationFsid: row.location_fsid,
        totalRows: Number(row.total_rows || 0),
        costedRows: Number(row.costed_rows || 0),
        missingCostRows: Number(row.missing_cost_rows || 0),
        otherLossesTotal: roundTo2(otherLossesTotal),
        costOfExpiryTotal: roundTo2(costOfExpiryTotal),
        lossesTotal,
        grossProfitTotal: roundOrNull(grossProfitTotal),
        lossesPercentOfGrossProfit: grossProfitTotal === null || grossProfitTotal <= 0
          ? null
          : roundTo2((lossesTotal / grossProfitTotal) * 100),
        previous3MonthsAverageDailyLosses: roundTo2(previous3MonthsAverageDailyLosses),
        currentAverageDailyLosses: roundTo2(currentAverageDailyLosses),
        lossesDeltaPercent: calculateDeltaPct(currentAverageDailyLosses, previous3MonthsAverageDailyLosses)
      }
    })
  }
}

async function getLossesMissingPricesSummary ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const params = [
    organisationId,
    fromDateUtc.toISOString(),
    toDateExclusiveUtc.toISOString()
  ]
  const whereClauses = []
  appendLossesScopeAndProductFilters({
    params,
    whereClauses,
    locationScope,
    productSku,
    serviceIds
  })

  const summaryQuery = `
      WITH filtered AS (
        SELECT
          li.location_id,
          li.product_sku_id,
          li.unit_cost
        FROM analytics.loss_indicators li
        INNER JOIN avocado.data_location dl
          ON dl.uuid = li.location_id
        WHERE li.organisation_id = $1
          AND li.reported_at >= $2::timestamptz
          AND li.reported_at < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        COUNT(DISTINCT product_sku_id) FILTER (
          WHERE product_sku_id IS NOT NULL
        )::int AS total_sku_count,
        COUNT(DISTINCT product_sku_id) FILTER (
          WHERE product_sku_id IS NOT NULL
            AND unit_cost IS NULL
        )::int AS missing_sku_count,
        COUNT(DISTINCT location_id) FILTER (
          WHERE location_id IS NOT NULL
            AND product_sku_id IS NOT NULL
            AND unit_cost IS NULL
        )::int AS facilities_with_missing_prices_count
      FROM filtered
  `

  const facilitiesQuery = `
      WITH missing_sku_per_facility AS (
        SELECT DISTINCT
          li.location_id,
          li.product_sku_id
        FROM analytics.loss_indicators li
        INNER JOIN avocado.data_location dl
          ON dl.uuid = li.location_id
        WHERE li.organisation_id = $1
          AND li.reported_at >= $2::timestamptz
          AND li.reported_at < $3::timestamptz
          AND li.unit_cost IS NULL
          AND li.product_sku_id IS NOT NULL
          ${whereClauses.join('\n')}
      ),
      expanded AS (
        SELECT
          dl.fsid AS location_fsid,
          dl.name_of_pharmacy AS location_name,
          dl.email AS contact_email,
          COALESCE(dl.owners_phone_number, dl.alt_phone_number) AS contact_number,
          m.product_sku_id AS product_sku,
          COALESCE(ps.display_name, m.product_sku_id) AS product_name
        FROM missing_sku_per_facility m
        INNER JOIN avocado.data_location dl
          ON dl.uuid = m.location_id
        LEFT JOIN avocado.data_productsku ps
          ON ps.old_sku_id = m.product_sku_id
      )
      SELECT
        location_fsid,
        location_name,
        contact_email,
        contact_number,
        json_agg(
          json_build_object(
            'productSku', product_sku,
            'productName', product_name
          )
          ORDER BY product_name ASC, product_sku ASC
        ) AS missing_products
      FROM expanded
      GROUP BY location_fsid, location_name, contact_email, contact_number
      ORDER BY location_name ASC NULLS LAST, location_fsid ASC
  `

  const [{ rows: [summary = {}] }, { rows: facilitiesRows }] = await Promise.all([
    pgConnection.query(summaryQuery, params),
    pgConnection.query(facilitiesQuery, params)
  ])

  const totalSkuCount = Number(summary.total_sku_count || 0)
  const missingSkuCount = Number(summary.missing_sku_count || 0)

  return {
    fromDate,
    toDate,
    organisationId,
    location: locationScope.location,
    missingSkuCount,
    totalSkuCount,
    missingSkuPercent: totalSkuCount > 0 ? (missingSkuCount / totalSkuCount) : null,
    facilitiesWithMissingPricesCount: Number(summary.facilities_with_missing_prices_count || 0),
    facilities: facilitiesRows.map((row) => ({
      locationFsid: row.location_fsid,
      locationName: row.location_name,
      contactEmail: row.contact_email,
      contactNumber: row.contact_number,
      missingProducts: Array.isArray(row.missing_products) ? row.missing_products : []
    }))
  }
}

module.exports = {
  getLossesTotals,
  getLossesSummary,
  getLossesByProductTable,
  getLossesByLocationTable,
  getLossesMissingPricesSummary
}
